import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth, useRole, hasRole, type Role } from '../../lib/auth.js';
import { api } from '../../lib/api.js';
import {
  Badge, Button, cx,
  IconDashboard, IconBlog, IconWidget, IconMedia, IconAnalytics, IconTeam,
  IconSettings, IconBuilding, IconHeartbeat, IconChevronDown, IconMenu,
  IconLogout, IconWarning, IconClose, IconListBullet,
} from '../ui/index.js';
import { PLAN_LABEL, ROLE_LABEL } from '../../lib/format.js';

interface NavItem {
  to: string; label: string; icon: ReactNode; minRole?: Role; end?: boolean;
}

const NAV: NavItem[] = [
  { to: '/', label: 'ภาพรวม', icon: <IconDashboard />, end: true },
  { to: '/blogs', label: 'บล็อก', icon: <IconBlog /> },
  { to: '/widgets', label: 'Widget', icon: <IconWidget /> },
  { to: '/media', label: 'คลังไฟล์', icon: <IconMedia /> },
  { to: '/analytics', label: 'สถิติ', icon: <IconAnalytics /> },
  { to: '/team', label: 'ทีมงาน', icon: <IconTeam />, minRole: 'ADMIN' },
  { to: '/audit', label: 'บันทึกกิจกรรม', icon: <IconListBullet />, minRole: 'ADMIN' },
  { to: '/settings', label: 'ตั้งค่า', icon: <IconSettings /> },
];

const LINK_BASE =
  'flex min-h-10 w-full items-center gap-3 overflow-hidden rounded-[10px] px-3 py-2 text-sm font-medium leading-5 transition-colors';

// ล็อกกรอบ icon ไว้ เพื่อให้ SVG ทุกทรงมี baseline และพื้นที่เท่ากัน
const NAV_ICON =
  'nav-icon grid h-8 w-8 shrink-0 place-items-center rounded-lg border ' +
  'border-[var(--line)] bg-[var(--surface)] leading-none text-[var(--ink-3)] shadow-[var(--shadow-xs)] ' +
  'transition-[background,color,border-color,box-shadow] duration-150 ' +
  '[&>svg]:block [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0';


function TenantSwitcher() {
  const { tenants, activeTenantId, setActiveTenant } = useAuth();
  const [open, setOpen] = useState(false);
  const active = tenants.find((t) => t.id === activeTenantId);
  if (!active) return null;

  const many = tenants.length > 1;

  async function pick(id: string) {
    setOpen(false);
    if (id === activeTenantId) return;
    setActiveTenant(id);
    // ออก access token ใหม่ที่ผูกกับบริษัทที่เลือก แล้วโหลดข้อมูลใหม่ทั้งหน้า
    try {
      const session = await api.post<never>('/v1/auth/switch-tenant', { tenantId: id });
      useAuth.getState().setSession(session as never);
    } catch { /* token เดิมยังใช้ได้ — resolveTenant อ่านจาก header แทน */ }
    window.location.reload();
  }

  return (
    <div className="relative">
      <button
        onClick={() => many && setOpen((o) => !o)}
        aria-expanded={many ? open : undefined}
        aria-haspopup={many ? 'menu' : undefined}
        className={cx(
          'flex w-full items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left transition-colors',
          'border-[var(--line)] bg-[var(--surface)]',
          many && 'hover:border-[var(--ink-3)]',
        )}
      >
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[var(--brand)] text-[13px] font-bold text-[var(--brand-ink)]">
          {active.companyName.trim().charAt(0)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold leading-tight text-[var(--ink)]">
            {active.companyName}
          </span>
          <span className="mt-0.5 block truncate text-[11px] text-[var(--ink-3)]">
            {PLAN_LABEL[active.planCode] ?? active.planCode} · {ROLE_LABEL[active.role] ?? active.role}
          </span>
        </span>
        {many && <IconChevronDown size={14} className="shrink-0 text-[var(--ink-3)]" />}
      </button>

      {open && many && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            role="menu"
            className="raised absolute left-0 right-0 top-full z-20 mt-1.5 animate-fade-in overflow-hidden p-1"
          >
            {tenants.map((t) => (
              <button
                key={t.id} role="menuitem" onClick={() => pick(t.id)}
                className={cx(
                  'flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors',
                  t.id === activeTenantId
                    ? 'bg-[var(--brand-soft)]'
                    : 'hover:bg-[var(--sunken)]',
                )}
              >
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-[var(--sunken)] text-[11px] font-bold text-[var(--ink-2)]">
                  {t.companyName.trim().charAt(0)}
                </span>
                <span className="min-w-0 flex-1 truncate text-[13px] text-[var(--ink)]">{t.companyName}</span>
                <Badge tone="gray">{ROLE_LABEL[t.role] ?? t.role}</Badge>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function AppShell() {
  const { user, clear } = useAuth();
  const role = useRole();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  // ปิดเมนูมือถืออัตโนมัติเมื่อจอกว้างพอจะเห็น sidebar ถาวรแล้ว
  useEffect(() => {
    if (!mobileOpen) return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const close = () => mq.matches && setMobileOpen(false);
    mq.addEventListener('change', close);
    return () => mq.removeEventListener('change', close);
  }, [mobileOpen]);

  async function logout() {
    try { await api.post('/v1/auth/logout'); } catch { /* ออกจากระบบฝั่ง client ให้ได้เสมอ */ }
    clear();
    navigate('/login', { replace: true });
  }

  const items = NAV.filter((n) => !n.minRole || hasRole(role, n.minRole));

  const linkClass = ({ isActive }: { isActive: boolean }) => cx(
    LINK_BASE,
    isActive
      ? 'bg-[var(--brand-soft)] text-[var(--brand)] [&_.nav-icon]:border-[color-mix(in_srgb,var(--brand)_28%,transparent)] [&_.nav-icon]:bg-[var(--brand)] [&_.nav-icon]:text-[var(--brand-ink)] [&_.nav-icon]:shadow-[0_3px_10px_color-mix(in_srgb,var(--brand)_22%,transparent)]'
      : 'text-[var(--ink-2)] hover:bg-[var(--sunken)] hover:text-[var(--ink)] hover:[&_.nav-icon]:border-[var(--ink-3)] hover:[&_.nav-icon]:text-[var(--ink)]',
  );

  return (
    <div className="flex min-h-full">
      <aside
        className={cx(
          'fixed inset-y-0 left-0 z-40 flex w-[248px] flex-col border-r border-[var(--line)] bg-[var(--surface)]',
          'transition-transform duration-200 lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex items-center justify-between gap-2 px-4 pb-3 pt-4">
          <span className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-[10px] bg-[var(--brand)] text-[15px] font-bold text-[var(--brand-ink)]">
              B
            </span>
            <span className="text-[17px] font-bold tracking-[-.02em] text-[var(--ink)]">Blogaris</span>
          </span>
          <button
            onClick={() => setMobileOpen(false)} aria-label="ปิดเมนู"
            className="rounded-lg p-1.5 text-[var(--ink-3)] hover:bg-[var(--sunken)] lg:hidden"
          >
            <IconClose size={16} />
          </button>
        </div>

        <div className="px-3 pb-3"><TenantSwitcher /></div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3" aria-label="เมนูหลัก">
          {items.map((item) => (
            <NavLink
              key={item.to} to={item.to} end={item.end}
              onClick={() => setMobileOpen(false)}
              className={linkClass}
            >
              <span className={NAV_ICON}>{item.icon}</span>
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
            </NavLink>
          ))}

        </nav>

        <div className="border-t border-[var(--line)] p-3">
          <div className="flex items-center gap-2.5 px-1 pb-2">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[var(--sunken)] text-[12px] font-bold text-[var(--ink-2)]">
              {user?.fullName?.trim().charAt(0) ?? '?'}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-medium text-[var(--ink)]">{user?.fullName}</span>
              <span className="block truncate text-[11px] text-[var(--ink-3)]">{user?.email}</span>
            </span>
          </div>
          <Button
            variant="ghost" size="sm" className="w-full justify-start"
            icon={<IconLogout size={15} />} onClick={logout}
          >
            ออกจากระบบ
          </Button>
        </div>
      </aside>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-[rgb(8_10_14/.5)] lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col lg:ml-[248px]">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-[var(--line)] bg-[color-mix(in_srgb,var(--bg)_88%,transparent)] px-4 backdrop-blur lg:hidden">
          <button
            onClick={() => setMobileOpen(true)} aria-label="เปิดเมนู"
            className="rounded-lg border border-[var(--line)] p-2 text-[var(--ink-2)]"
          >
            <IconMenu size={16} />
          </button>
          <span className="font-bold text-[var(--ink)]">Blogaris</span>
        </header>

        {!user?.emailVerified && <VerifyBanner />}

        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="mx-auto w-full max-w-7xl"><Outlet /></div>
        </main>
      </div>
    </div>
  );
}

function VerifyBanner() {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-[color-mix(in_srgb,var(--warn)_35%,transparent)] bg-[var(--warn-soft)] px-4 py-2.5 text-sm text-[var(--warn)] sm:px-6 lg:px-8">
      <IconWarning size={16} className="shrink-0" />
      <span className="flex-1 min-w-[200px]">
        อีเมลของคุณยังไม่ได้ยืนยัน — บางฟีเจอร์อาจถูกจำกัดจนกว่าจะยืนยัน
      </span>
      <Button
        size="sm" variant="outline" loading={busy} disabled={sent}
        onClick={async () => {
          setBusy(true);
          try { await api.post('/v1/auth/resend-verification'); setSent(true); }
          finally { setBusy(false); }
        }}
      >
        {sent ? 'ส่งอีเมลแล้ว' : 'ส่งอีเมลยืนยันอีกครั้ง'}
      </Button>
    </div>
  );
}
